import React, { useState } from 'react';
import DropdownWithSearch from 'components/dropdown-with-search/DropdownWithSearch';
import {
  CommissionBaseType,
  CommissionRuleConditionIfFilterOptions,
  CommissionStatus,
  CommissionStatusFilterOptions
} from '../constants';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam, selectMerchantCustomizations } from 'containers/app/appSlice';
import { useGetAssignableMembersQuery } from 'containers/jobs/api';
import { getTextForFilter } from 'containers/dispatch/utils';
import { getCommissionBases } from 'containers/settings/utils';
import { CommissionBasesSourceType } from 'containers/settings/constants';

export type CommissionRulesFilterValue = {
  status: CommissionStatus[];
  assignedTo: string[];
  condition: string[];
  base: CommissionBaseType[];
};

type CommissionRulesFilterProps = {
  filter: CommissionRulesFilterValue;
  setFilter: (filter: CommissionRulesFilterValue) => void;
};

const CommissionRulesFilter: React.FC<CommissionRulesFilterProps> = ({ filter, setFilter }) => {
  const currentTeam = useAppSelector(selectCurrentTeam);
  const { teamConfigs } = useAppSelector(selectMerchantCustomizations) || {};
  const { hasPaymentMethodInCommissionRules } = teamConfigs || {};

  const [selectedStatus, setSelectedStatus] = useState(filter.status);
  const [selectedStatusDraft, setSelectedStatusDraft] = useState(null);
  const [selectedAssignedTo, setSelectedAssignedTo] = useState(filter.assignedTo);
  const [selectedAssignedToDraft, setSelectedAssignedToDraft] = useState(null);
  const [selectedCondition, setSelectedCondition] = useState(filter.condition);
  const [selectedConditionDraft, setSelectedConditionDraft] = useState(null);
  const [selectedBase, setSelectedBase] = useState(filter.base);
  const [selectedBaseDraft, setSelectedBaseDraft] = useState(null);
  const [baseFilterSearch, setBaseFilterSearch] = useState('');
  const [assignedToFilterSearch, setAssignedToFilterSearch] = useState('');

  const { data: teamMembers } = useGetAssignableMembersQuery(currentTeam?.id?.toString() || '');

  const handleStatusChange = (status: CommissionStatus[]) => {
    setSelectedStatus(status);
    setFilter({ ...filter, status });
  };

  const handleAssignedToChange = (assignedTo: string[]) => {
    setSelectedAssignedTo(assignedTo);
    setFilter({ ...filter, assignedTo });
  };

  const handleConditionChange = (condition: string[]) => {
    setSelectedCondition(condition);
    setFilter({ ...filter, condition });
  };

  const handleBaseChange = (base: CommissionBaseType[]) => {
    setSelectedBase(base);
    setFilter({ ...filter, base });
  };

  const getCommissionRuleConditionIfFilterOptions = () => {
    if (!hasPaymentMethodInCommissionRules) {
      return CommissionRuleConditionIfFilterOptions.filter(option => option.id !== 'PAYMENT_METHOD');
    }
    return CommissionRuleConditionIfFilterOptions;
  };

  return (
    <div className="mb-4 flex w-full flex-col gap-4 md:flex-row md:items-center">
      <DropdownWithSearch
        data={CommissionStatusFilterOptions}
        dataKey="name"
        dataIdKey="id"
        selectedArray={selectedStatus}
        setSelectedArray={handleStatusChange}
        selectedDraftArray={selectedStatusDraft}
        setSelectedDraftArray={setSelectedStatusDraft}
        filterPlaceholderText="Select status"
        buttonPrimaryText="Status"
        buttonSecondaryText={getTextForFilter(CommissionStatusFilterOptions, selectedStatus, 'id', 'name')}
        allowSearch={false}
        filterId="commission-filter-status"
      />
      <DropdownWithSearch
        data={teamMembers?.data?.teamMemberList ?? []}
        dataKey="name"
        dataIdKey="merchantId"
        selectedArray={selectedAssignedTo}
        setSelectedArray={handleAssignedToChange}
        selectedDraftArray={selectedAssignedToDraft}
        setSelectedDraftArray={setSelectedAssignedToDraft}
        filterPlaceholderText="Select assigned to"
        buttonPrimaryText="Assigned to"
        buttonSecondaryText={getTextForFilter(
          teamMembers?.data?.teamMemberList ?? [],
          selectedAssignedTo,
          'merchantId',
          'name'
        )}
        allowSearch={true}
        filterId="commission-filter-assigned-to"
        searchText={assignedToFilterSearch}
        setSearchText={setAssignedToFilterSearch}
        searchPlaceHolderText="Search member"
      />
      <DropdownWithSearch
        data={getCommissionRuleConditionIfFilterOptions()}
        dataKey="name"
        dataIdKey="id"
        selectedArray={selectedCondition}
        setSelectedArray={handleConditionChange}
        selectedDraftArray={selectedConditionDraft}
        setSelectedDraftArray={setSelectedConditionDraft}
        filterPlaceholderText="Select condition"
        buttonPrimaryText="Condition"
        buttonSecondaryText={getTextForFilter(CommissionRuleConditionIfFilterOptions, selectedCondition, 'id', 'name')}
        allowSearch={false}
        filterId="commission-filter-condition"
      />
      <DropdownWithSearch
        data={getCommissionBases({ type: CommissionBasesSourceType.COMMISSION })}
        dataKey="label"
        dataIdKey="key"
        selectedArray={selectedBase}
        setSelectedArray={handleBaseChange}
        selectedDraftArray={selectedBaseDraft}
        setSelectedDraftArray={setSelectedBaseDraft}
        filterPlaceholderText="Select base"
        buttonPrimaryText="Base"
        buttonSecondaryText={getTextForFilter(
          getCommissionBases({ type: CommissionBasesSourceType.COMMISSION }),
          selectedBase,
          'key',
          'label'
        )}
        allowSearch={true}
        filterId="commission-filter-base"
        searchText={baseFilterSearch}
        setSearchText={setBaseFilterSearch}
        searchPlaceHolderText="Search base"
      />
    </div>
  );
};

export default CommissionRulesFilter;
